import React, { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';
import TextInput from '../components/TextInput';
import PasswordInput from '../components/PasswordInput';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import { useAuth } from '../context/AuthContext';
import { resendVerification } from '../api/authApi';
import { ApiError } from '../types/auth.types';
import { homeForRole } from '../utils/roleHome';
import styles from './LoginPage.module.css';

interface LocationState {
  from?: { pathname: string };
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  function validate(): boolean {
    const errors: { email?: string; password?: string } = {};
    if (!email.trim()) {
      errors.email = 'Ingresá tu email.';
    }
    if (!password) {
      errors.password = 'Ingresá tu contraseña.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setGeneralError(null);
    setEmailNotVerified(false);
    setResendSent(false);

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const usuario = await login({ email, password });
      const state = location.state as LocationState | null;
      const redirectTo = state?.from?.pathname ?? homeForRole(usuario.role);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (axios.isAxiosError<ApiError>(error)) {
        const data = error.response?.data;
        if (data?.codigo === 'PASSWORD_EXPIRED') {
          navigate('/change-password?expired=true', { replace: true });
          return;
        }
        if (data?.codigo === 'EMAIL_NOT_VERIFIED') {
          setEmailNotVerified(true);
        }
        setGeneralError(data?.mensaje ?? 'Ocurrió un error al iniciar sesión.');
      } else {
        setGeneralError('Ocurrió un error al iniciar sesión.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    if (!email.trim()) return;
    setResending(true);
    try {
      await resendVerification(email.trim());
      setResendSent(true);
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout
      title="Iniciar sesión"
      subtitle="Laboratorio Sagrado Corazón"
      logoSize={150}
      footer={<span>© 2026 SC Lab. Todos los derechos reservados.</span>}
    >
      <form onSubmit={handleSubmit} noValidate>
        {generalError && <ErrorBanner message={generalError} onClose={() => setGeneralError(null)} />}

        {emailNotVerified && (
          resendSent ? (
            <p className={styles.resendSentText}>Te reenviamos el enlace de confirmación.</p>
          ) : (
            <button
              type="button"
              className={styles.resendLink}
              onClick={handleResendVerification}
              disabled={resending}
            >
              {resending ? 'Enviando...' : 'Reenviar email de verificación'}
            </button>
          )
        )}

        <TextInput
          id="email"
          label="Email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          error={fieldErrors.email}
        />

        <PasswordInput
          id="password"
          label="Contraseña"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          error={fieldErrors.password}
        />

        <div className={styles.forgotLinkWrapper}>
          <Link to="/forgot-password" className={styles.forgotLink}>
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        <Button type="submit" loading={loading} loadingText="Ingresando...">
          Ingresar
        </Button>
      </form>

      <div className={styles.forgotLinkWrapper} style={{ justifyContent: 'center', marginTop: 16 }}>
        <Link to="/register" className={styles.forgotLink}>
          ¿No tenés cuenta? Crear cuenta
        </Link>
      </div>
    </AuthLayout>
  );
}
